import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import {
  DATE_STYLE_LABELS,
  DATE_STYLE_OPTIONS,
  TEXT_CASE_LABELS,
  TEXT_CASE_OPTIONS,
  TOKEN_METADATA,
  formatTransformSummary,
  generateTitlePreview,
  getPalettePreview,
  getTextTransformSpaceReplacementError,
  getTransformKind,
} from './titleTransformUtils';

const TitleBuilder = ({
  titleComponents,
  isEditingConfig,
  editingTitleComponent,
  setEditingTitleComponent,
  onAddToken,
  onAddLiteral,
  onMoveComponent,
  onDeleteComponent,
  onTextCaseChange,
  onSpaceReplacementChange,
  onDateStyleChange,
  onLiteralEdit,
}) => (
  <div className="config-subsection">
    <h3>Document Title Format</h3>
    <div className="title-builder-description">
      Build document titles from tokens, separators, and per-token text or date formatting.
    </div>

    <div className="title-builder-area">
      {titleComponents.length === 0 && (
        <div className="empty-builder-message">
          Click tokens below to add them to your title format
        </div>
      )}

      {titleComponents.map((component, index) => {
        const transformKind = component.type === 'TOKEN'
          ? getTransformKind(component.token)
          : null;
        const transform = component.transform;
        const isEditing = isEditingConfig;
        const spaceReplacementError =
          transformKind === 'TEXT' && transform
            ? getTextTransformSpaceReplacementError(transform)
            : null;

        return (
          <div
            key={index}
            className={`title-component ${component.type === 'TOKEN' ? 'token' : 'literal'} ${
              !isEditing ? 'disabled' : ''
            } ${component.type === 'TOKEN' && transformKind !== 'NONE' ? 'has-transform' : ''}`}
          >
            <div className="title-component-main">
              {isEditing && (
                <div className="reorder-controls">
                  <button
                    className="reorder-btn"
                    onClick={() => onMoveComponent(index, -1)}
                    disabled={index === 0}
                    title="Move left"
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                  </button>
                </div>
              )}

              {component.type === 'TOKEN' ? (
                <div className="token-component-content">
                  <span className="component-label">
                    {TOKEN_METADATA[component.token]?.label || component.token}
                  </span>
                  {transform && (
                    <span className="transform-summary">
                      {formatTransformSummary(transform, component.token)}
                    </span>
                  )}
                </div>
              ) : (
                <>
                  {editingTitleComponent === index ? (
                    <input
                      type="text"
                      className="literal-input"
                      value={component.value}
                      onChange={(e) => onLiteralEdit(index, e.target.value)}
                      onBlur={() => setEditingTitleComponent(null)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          setEditingTitleComponent(null);
                        }
                      }}
                      style={{ width: `${Math.max((component.value || '').length * 10 + 20, 40)}px` }}
                      autoFocus
                    />
                  ) : (
                    <span
                      className="component-label literal-text"
                      onClick={() => isEditing && setEditingTitleComponent(index)}
                      title={isEditing ? 'Click to edit' : ''}
                    >
                      {component.value || '(empty)'}
                    </span>
                  )}
                </>
              )}

              {isEditing && (
                <div className="action-controls">
                  <button
                    className="reorder-btn"
                    onClick={() => onMoveComponent(index, 1)}
                    disabled={index === titleComponents.length - 1}
                    title="Move right"
                  >
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                  <button
                    className="delete-component-btn"
                    onClick={() => onDeleteComponent(index)}
                    title="Remove"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}
            </div>

            {isEditing && component.type === 'TOKEN' && transformKind === 'TEXT' && transform && (
              <div className="title-transform-controls">
                <label className="transform-field">
                  <span>Case</span>
                  <select
                    value={transform.textCase || 'UPPER'}
                    onChange={(e) => onTextCaseChange(index, e.target.value)}
                  >
                    {TEXT_CASE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {TEXT_CASE_LABELS[option]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className={`transform-field ${spaceReplacementError ? 'has-error' : ''}`}>
                  <span>Spaces</span>
                  <input
                    type="text"
                    className="transform-input"
                    value={transform.spaceReplacement ?? ''}
                    onChange={(e) => onSpaceReplacementChange(index, e.target.value)}
                    placeholder="_"
                    aria-invalid={Boolean(spaceReplacementError)}
                  />
                  {spaceReplacementError && (
                    <span className="transform-field-error">{spaceReplacementError}</span>
                  )}
                </label>
              </div>
            )}

            {isEditing && component.type === 'TOKEN' && transformKind === 'DATE' && transform && (
              <div className="title-transform-controls">
                <label className="transform-field">
                  <span>Format</span>
                  <select
                    value={transform.dateStyle || 'DD_MMM_YYYY'}
                    onChange={(e) => onDateStyleChange(index, e.target.value)}
                  >
                    {DATE_STYLE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {DATE_STYLE_LABELS[option]}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            )}
          </div>
        );
      })}
    </div>

    {isEditingConfig && (
      <div className="token-palette">
        <div className="palette-label">Click to add:</div>
        <div className="palette-tokens">
          {Object.keys(TOKEN_METADATA).map(tokenKey => (
            <button
              key={tokenKey}
              className="palette-token"
              onClick={() => onAddToken(tokenKey)}
              title={`Add ${TOKEN_METADATA[tokenKey].label} (example: ${getPalettePreview(tokenKey)})`}
            >
              <FontAwesomeIcon icon={faPlus} className="add-icon" />
              {TOKEN_METADATA[tokenKey].label}
            </button>
          ))}
          <button
            className="palette-token literal-palette"
            onClick={onAddLiteral}
            title="Add custom separator text"
          >
            <FontAwesomeIcon icon={faPlus} className="add-icon" />
            Separator
          </button>
        </div>
      </div>
    )}

    <div className="title-preview-container">
      <div className="preview-label">Preview:</div>
      <div className="title-preview">
        {titleComponents.length > 0 ? generateTitlePreview(titleComponents) : 'No components added yet'}
      </div>
    </div>
  </div>
);

export default TitleBuilder;
